"use client";

import React, { useState, useMemo, useEffect } from "react";
// import SlideToggle from "../../ui/SlideToggle";
// import { TfiViewList, TfiViewGrid } from "react-icons/tfi";
import FilterSortPanelUI from "@components/collection/filter-group-ui";
import { Microphone } from "@/types";

// Alphabetical, but the catch-all always sits last
const byName = (a: string, b: string) =>
  a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b);

// Shorthands people type instead of the full country name held in Hygraph
const countryAliases: Record<string, string> = {
  us: "united states",
  usa: "united states",
  america: "united states",
  american: "united states",
  uk: "united kingdom",
  gb: "united kingdom",
  britain: "united kingdom",
  british: "united kingdom",
  england: "united kingdom",
};

const matchesSearch = (mic: Microphone, query: string) => {
  if (query === "") return true;

  const countries = (mic.countryOfManufacture ?? [])
    .filter(Boolean)
    .map((c) => c.trim().toLowerCase());

  // A bare "us" or "uk" means the country, not those letters inside a model name
  const alias = countryAliases[query];
  if (alias) return countries.includes(alias);

  // Match a country from its start, or from the start of any word within it,
  // so both "united sta" and "kingdom" find United Kingdom
  const matchesCountry = countries.some(
    (country) =>
      country.startsWith(query) ||
      country.split(" ").some((word) => word.startsWith(query)),
  );

  return (
    matchesCountry ||
    `${mic.manufacturer} ${mic.model}`.toLowerCase().includes(query)
  );
};

interface FilterProps {
  microphones: Microphone[];
  updateFunction: (updatedMicrophones: Microphone[]) => void;
  horizontal?: boolean;
}

const FilterSortPanel = ({ microphones, updateFunction, horizontal = false }: FilterProps) => {
  const [sortBy, setSortBy] = useState("name");
  const [filters, setFilters] = useState<{
    type: string[];
    pattern: string[];
    yearRange?: [number, number];
  }>({
    type: [],
    pattern: [],
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Generate unique filter values
  const filterOptions = useMemo(() => {
    const years = microphones
      .map(m => m.productionStartYear)
      .filter(year => year != null);
    
    const minYear = years.length > 0 ? Math.min(...years) : 1920;
    const maxYear = years.length > 0 ? Math.max(...years) : 2024;
    
    return {
      type: [...new Set(microphones.map((m) => m.microphoneType))].sort(byName),
      pattern: [...new Set(microphones.map((m) => m.polarPattern))].sort(byName),
      yearRange: [minYear, maxYear] as [number, number],
    };
  }, [microphones]);

  // Initialize yearRange when filterOptions change
  useEffect(() => {
    if (filterOptions.yearRange && !filters.yearRange) {
      setFilters(prev => ({
        ...prev,
        yearRange: filterOptions.yearRange
      }));
    }
  }, [filterOptions.yearRange, filters.yearRange]);

  // Apply filters and search
  const filteredMicrophones = useMemo(() => {
    return microphones.filter((mic) => {
      const matchesFilters =
        (filters.type.length === 0 || filters.type.includes(mic.microphoneType)) &&
        (filters.pattern.length === 0 || filters.pattern.includes(mic.polarPattern)) &&
        (!filters.yearRange || (mic.productionStartYear >= filters.yearRange[0] && mic.productionStartYear <= filters.yearRange[1]));

      return (
        matchesFilters &&
        matchesSearch(mic, searchTerm.trim().toLowerCase())
      );
    });
  }, [microphones, filters, searchTerm]);

  // Apply sorting
  const sortedMicrophones = useMemo(() => {
    return [...filteredMicrophones].sort((a, b) => {
      if (sortBy === "name") {
        const nameA = `${a.manufacturer} ${a.model}`.toLowerCase();
        const nameB = `${b.manufacturer} ${b.model}`.toLowerCase();
        return nameA.localeCompare(nameB);
      }
      if (sortBy === "year")
        return a.productionStartYear - b.productionStartYear;
      if (sortBy === "type")
        return a.microphoneType.localeCompare(b.microphoneType);
      if (sortBy === "pattern")
        return a.polarPattern.localeCompare(b.polarPattern);
      if (sortBy === "dateAdded") {
        // Sort by createdAt date (newest first)
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      if (sortBy === "dateAddedOldest") {
        // Sort by createdAt date (oldest first)
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      return 0;
    });
  }, [filteredMicrophones, sortBy]);

  function handleFilterChange(filterGroup: string, value: string, checked: boolean) {
    setFilters((prev) => {
      const currentValues = prev[filterGroup as keyof typeof prev] as string[];
      if (checked) {
        return { ...prev, [filterGroup]: [...currentValues, value] };
      } else {
        return { ...prev, [filterGroup]: currentValues.filter(v => v !== value) };
      }
    });
  }

  function handleYearRangeChange(range: [number, number]) {
    setFilters(prev => ({
      ...prev,
      yearRange: range
    }));
  }

  function handleClearFilters() {
    setFilters({
      type: [],
      pattern: [],
      yearRange: filterOptions.yearRange,
    });
    setSearchTerm("");
    setSortBy("name");
  }
  // Update the parent when filteredMicrophones change
  useEffect(() => {
    if (updateFunction && typeof updateFunction === "function") {
      updateFunction(sortedMicrophones);
    }
  }, [sortedMicrophones, updateFunction]);
  return (
    <div className={horizontal ? "w-full" : "p-5 grid grid-cols-1 md:grid-cols-[200px,1fr] gap-3"}>
      {/* Filter & Sort UI */}
      <FilterSortPanelUI
        filters={filters}
        onFilterChange={handleFilterChange}
        filterOptions={filterOptions}
        sortBy={sortBy}
        onSetSortBy={setSortBy}
        horizontal={horizontal}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onClearFilters={handleClearFilters}
        onYearRangeChange={handleYearRangeChange}
      />

      {!horizontal && (
        <div>
          {/* <MicrophoneList
            microphones={sortedMicrophones}
            listViewStyle={listOrCard}
          /> */}
        </div>
      )}
    </div>
  );
};

export default FilterSortPanel;
